<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * List item wrapper with interactive state layer and active/selected state support.
 *
 * @status internal
 * @group navigation
 * @within hr-list
 * @see https://quasar.dev/vue-components/list-and-list-items Quasar q-item API
 */
defineOptions({ name: 'HrItem' });

const props = defineProps({
  /**
   * Makes the item interactive — adds focus/hover/press state layer and
   * keyboard focusability. Required for any item with a click handler.
   */
  clickable: {
    type: Boolean,
    default: false,
  },
  /**
   * Marks the item as the currently active/selected item.
   * Renders with secondary-container background per M3 spec.
   * Defaults to null so Quasar's router-link active detection is not suppressed
   * when a :to prop is present.
   */
  active: {
    type: Boolean,
    default: null,
  },
  /**
   * HTML tag to render the item as. Use 'label' to wrap form controls
   * (checkboxes, radios) so the entire row is clickable.
   */
  tag: {
    type: String,
    default: 'div',
  },
  /**
   * Indent level for nested items. Each level adds a fixed left padding offset.
   * Common values: 0.5, 1.
   */
  insetLevel: {
    type: Number,
    default: null,
  },
  /** Reduces the item height to the dense density level. */
  dense: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the item. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Applies the error color role — use for destructive actions in menus. */
  destructive: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders the item as an M3 "segmented" list row — a filled, rounded card with its own
   * surface-container background, so each row reads as a discrete card at rest (not just on hover).
   * The segmented style is a visual choice and doesn't change behavior.
   */
  segmented: {
    type: Boolean,
    default: false,
  },
  /**
   * Removes the row's horizontal inset so it sits flush with its container.
   *
   * A row insets itself, by 8px as a segmented card and by its content padding as a plain row,
   * which is right for a list that owns its surface. It is wrong wherever the list sits inside
   * another layout — a form grid, or a card's content — where every neighboring control and line of
   * text is flush to the column and the inset reads as misalignment.
   */
  flush: {
    type: Boolean,
    default: false,
  },
  /**
   * De-emphasises the row for archived / historical / inactive entries. Lowers the text to the
   * on-surface-variant role (and a segmented card to a lower container tone), so it reads as
   * secondary without using opacity — the content stays AA-legible rather than fading out.
   */
  muted: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props passed through to q-item for backwards compatibility with the legacy
   * theme. Ignored when a design system theme is active.
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

/*
 * `segmented` and its inset outlive the gate the rest of the M3 treatment sits behind: they are
 * structure, not color, and a row that stops being a card in legacy reads as a different component.
 * `destructive` and `muted` stay behind it — those ARE color, and legacy expresses them its own way.
 */
const classes = computed(() => [
  hasTheme.value && 'hr-item',
  hasTheme.value && props.destructive && 'hr-item--destructive',
  props.segmented && 'hr-item--segmented',
  props.flush && 'hr-item--flush',
  hasTheme.value && props.muted && 'hr-item--muted',
]);

const effectiveProps = computed(() => {
  const base = {
    clickable: props.clickable,
    ...(props.active !== null && { active: props.active }),
    tag: props.tag,
    ...(props.insetLevel !== null && { insetLevel: props.insetLevel }),
    dense: props.dense,
    disable: props.disable,
  };
  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-item
    v-bind="effectiveProps"
    :class="classes"
  >
    <!-- @slot default One or more HrItemSection elements forming the item's layout. -->
    <slot />
  </q-item>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Base ───────────────────────────────────────────────────────── */
/*
 * --hr-on-container is set by HrListGroup when the item is inside a tonal
 * container. Falls back to on-surface when no container is present.
 */
.hr-item {
  color: var(--hr-on-container, var(--app-on-surface));
}

/* ── State layer ────────────────────────────────────────────────── */
/*
 * Quasar renders a .q-focus-helper child div on clickable items that
 * serves as the state layer (hover/focus/press overlay). We recolor it
 * to the M3 on-surface token and apply M3 opacities: 8% hover, 12% focus/press.
 * The ::before pseudo adds a secondary overlay in Quasar — zero it out so
 * we have full control over opacity.
 *
 * The focus layer keys off :focus-visible (keyboard focus) rather than :focus-within
 * so a mouse click — or focus returned when a dialog the item opened closes — does not
 * leave a lingering overlay, while keyboard navigation still shows the focus state.
 */
.hr-item > :deep(.q-focus-helper) {
  background: var(--hr-on-container, var(--app-on-surface));
}

.hr-item > :deep(.q-focus-helper::before),
.hr-item > :deep(.q-focus-helper::after) {
  opacity: 0 !important;
}

/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-item:not(.disabled):hover > :deep(.q-focus-helper) {
  opacity: 0.08;
}

.hr-item:not(.disabled):focus-visible > :deep(.q-focus-helper) {
  opacity: 0.12;
}

.hr-item:not(.disabled):active > :deep(.q-focus-helper) {
  opacity: 0.12;
}

/* ── Dense (direct prop) ────────────────────────────────────────── */
/*
 * When dense is set directly on the component Quasar adds q-item--dense.
 * Drop from body-large (16sp) to label-large (14sp) for M3 menu typography.
 */
.hr-item.q-item--dense :deep(.hr-item-label) {
  font-size:      var(--app-type-label-large-size);
  line-height:    var(--app-type-label-large-line-height);
  font-weight:    var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

/* ── Active ─────────────────────────────────────────────────────── */
/*
 * Two sources of active state:
 *   q-item--active      — set when active prop is true (e.g. non-router use)
 *   q-router-link--active — set by Quasar when :to route matches (router use)
 * Both get M3 secondary-container treatment.
 */
.hr-item.q-item--active,
.hr-item.q-router-link--active {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

/* State layer on active items uses on-secondary-container color */
.hr-item.q-item--active > :deep(.q-focus-helper),
.hr-item.q-router-link--active > :deep(.q-focus-helper) {
  background: var(--app-on-secondary-container);
}

/* ── Destructive ────────────────────────────────────────────────── */
.hr-item--destructive {
  color: var(--app-error);
}

.hr-item--destructive > :deep(.q-focus-helper) {
  background: var(--app-error);
}

/* ── Disabled ───────────────────────────────────────────────────── */
/* Override Quasar's blanket opacity: 0.75 !important with M3's 38% */
.hr-item.disabled {
  opacity: 0.38 !important;
}

/* ── Segmented (filled card) ─────────────────────────────────────── */
/*
 * M3 "segmented" list row: a filled surface-container card, rounded, so each row reads as a
 * discrete card at rest on every breakpoint. The horizontal inset belongs to the container.
 */
/*
 * Vertical margin only: the gap between cards is the item's business, but the inset from the
 * surface edge is the container's, and an item that indents itself cannot line up with the search
 * field, header or label it sits under.
 */
.hr-item--segmented {
  margin: 2px 0;
  border-radius: var(--app-shape-corner-medium);
  overflow: hidden;
  background: var(--app-surface-container-high);
}

/*
 * Flush variant: the card keeps its fill, radius and vertical rhythm and gives up only the
 * horizontal inset. `margin-inline` rather than left/right, because RTL is enabled.
 */
.hr-item--segmented.hr-item--flush {
  margin-inline: 0;
}

/* A plain row's inset is its own content padding, so flush gives that up instead. */
.hr-item--flush:not(.hr-item--segmented) {
  padding-inline: 0;
}

/* ── Muted (de-emphasized) ──────────────────────────────────────── */
/*
 * Lower-emphasis row for archived / historical / inactive entries. De-emphasis is by tonal role,
 * not opacity: the text drops to on-surface-variant and a segmented card to a lower container tone,
 * so it reads as secondary while the content stays AA-legible (opacity would fade it toward the
 * background and risk dropping under the contrast minimum).
 */
.hr-item--muted {
  color: var(--app-on-surface-variant);
}

.hr-item--muted.hr-item--segmented {
  background: var(--app-surface-container);
}

</style>

<style lang="scss">

/*
 * Not scoped — Vue 3's provide/inject for Quasar's dense context only reaches
 * direct children of q-list, not slot content passed through HrList. CSS
 * cascade reaches the DOM descendants regardless. [data-theme] ensures these
 * rules only apply when a design system theme is active.
 */
[data-theme] .hr-list--dense .hr-item .hr-item-label {
  font-size:      var(--app-type-label-large-size);
  line-height:    var(--app-type-label-large-line-height);
  font-weight:    var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

[data-theme] .hr-item--destructive .hr-item-label,
[data-theme] .hr-item--destructive .hr-icon {
  color: var(--app-error);
}

</style>
